import React, { useState, useEffect, useRef } from "react";
import {
  Typography,
  Container,
  Grid,
  Box,
  Backdrop,
  CircularProgress,
  Button,
  Stepper,
  IconButton,
  InputAdornment,
  Step,
  StepLabel,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import YouTubeIcon from "@mui/icons-material/YouTube";
import LinkIcon from "@mui/icons-material/Link";
import BlurryBackdrop from "./BlurryBackdrop";
import HistoryIcon from "@mui/icons-material/History";
import ClassIcon from "@mui/icons-material/Class";

import CancelIcon from "@mui/icons-material/Cancel";
import LoadingDialog from "./LoadingDialog";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import WavesIcon from "@mui/icons-material/Waves";
import NatureIcon from "@mui/icons-material/Nature";
import { useSnackbar } from "./SnackbarHandler";
import PublicIcon from "@mui/icons-material/Public";
import { trackLesson } from "./functions";
import LanguageIcon from "@mui/icons-material/Language";
import ScienceIcon from "@mui/icons-material/Science";
import { subjectVideosForYouTube, subjectVideos } from "./functions";
import TranslateIcon from "@mui/icons-material/Translate";
import { East as ArrowIcon } from "@mui/icons-material";
import CalculateIcon from "@mui/icons-material/Calculate";
import PeopleIcon from "@mui/icons-material/People";
import MosqueIcon from "@mui/icons-material/Mosque";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import BiotechIcon from "@mui/icons-material/Biotech";
import ThemeFlowBox from "./ThemeFlowBox";
import VideoCard from "./VideoCard";
import Logger from "./Logger";
import CheckIcon from "@mui/icons-material/Check";
import RefreshIcon from "@mui/icons-material/Refresh";

import PsychologyIcon from "@mui/icons-material/Psychology"; // For Philosophy
import HistoryEduIcon from "@mui/icons-material/HistoryEdu"; // For History
import PaletteIcon from "@mui/icons-material/Palette"; // For Art
import AutoStoriesIcon from "@mui/icons-material/AutoStories"; // For Literature
import GroupsIcon from "@mui/icons-material/Groups"; // For Sociology
import AccountBalanceIcon from "@mui/icons-material/AccountBalance"; // For Political Theory
import DiversityIcon from "@mui/icons-material/Diversity3"; // For African American History
import ThermostatIcon from "@mui/icons-material/Thermostat"; // For Climate

const steps = ["Välj ämne", "Välj video", "Video hämtad"];

const schoolSubjectsForYouTube = [
  { value: "filosofi", label: "Filosofi", Icon: PsychologyIcon },
  { value: "historia", label: "Historia", Icon: HistoryEduIcon },
  { value: "konst", label: "Konst", Icon: PaletteIcon },
  { value: "litteratur", label: "Litteratur", Icon: AutoStoriesIcon },
  { value: "sociologi", label: "Sociologi", Icon: GroupsIcon },
  {
    value: "politisk_teori",
    label: "Politisk teori",
    Icon: AccountBalanceIcon,
  },
  {
    value: "afroamerikanskhistoria",
    label: "Afroamerikansk historia",
    Icon: DiversityIcon,
  },
  { value: "klimat", label: "Klimat", Icon: ThermostatIcon },
  { value: "biologi", label: "Biologi", Icon: BiotechIcon },
];

const schoolSubjects = [
  { value: "religionskunskap", label: "Religionskunskap", Icon: MosqueIcon },
  { value: "historia", label: "Historia", Icon: HistoryIcon },
  { value: "litteratur", label: "Litteratur", Icon: MenuBookIcon },

  { value: "naturkunskap", label: "Naturkunskap", Icon: NatureIcon },

  // { value: 'fysik', label: 'Fysik', Icon: WavesIcon },
  // { value: 'biologi', label: 'Biologi', Icon: NatureIcon },
  // { value: 'geografi', label: 'Geografi', Icon: PublicIcon },
  // { value: 'engelska', label: 'Engelska', Icon: LanguageIcon },
  // { value: 'kemi', label: 'Kemi', Icon: ScienceIcon },
  // { value: 'modersmål', label: 'Modersmål', Icon: PublicIcon },

  // { value: 'samhällskunskap', label: 'Samhällskunskap', Icon: PeopleIcon },
  // { value: 'svenska', label: 'Svenska', Icon: TranslateIcon },
  // { value: 'matematik', label: 'Matematik', Icon: CalculateIcon },
  // { value: 'teknik', label: 'Teknik', Icon: BiotechIcon },
];

export const YouTubeSelector = ({
  onTranscriptChange,
  urplay = false,
  forQuiz,
  forFlashcards,
  forPodcast,
  user,
  forPowerPoint,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [isLoadingTranscript, setIsLoadingTranscript] = useState(false);
  const [transcriptFetched, setTranscriptFetched] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const { showSnackbar } = useSnackbar();
  const [selectionMode, setSelectionMode] = useState(null); // null, 'custom', or 'curated'

  const [openUrlDialog, setOpenUrlDialog] = useState(false);
  const [customUrl, setCustomUrl] = useState("");
  const [showCustomVideoPreview, setShowCustomVideoPreview] = useState(false);
  const [customVideo, setCustomVideo] = useState(null);

  const [isCancelled, setIsCancelled] = useState(false);
  const abortControllerRef = useRef(null);

  const subjects = urplay ? schoolSubjects : schoolSubjectsForYouTube;
  const videos = urplay ? subjectVideos : subjectVideosForYouTube;

  const handleCancel = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setIsCancelled(true);
    setIsLoadingTranscript(false);
    showSnackbar("Hämtningen av YouTube-videon avbruten.", "info");
  };

  const handleUrlDialogOpen = () => {
    setOpenUrlDialog(true);
  };

  const handleUrlDialogClose = () => {
    setOpenUrlDialog(false);
    setCustomUrl("");
  };

  const handleCustomUrlSubmit = () => {
    if (customUrl) {
      const videoId = extractVideoId(customUrl);
      if (videoId) {
        const newVideo = {
          title: "Video hämtad!",
          url: customUrl,
        };
        setCustomVideo(newVideo);
        setShowCustomVideoPreview(true);
        handleUrlDialogClose();
      } else {
        showSnackbar(
          "Inkorrekt YouTube-URL. Kontrollera och försök igen.",
          "error",
        );
      }
    } else {
      showSnackbar("Skriv in en korrekt YouTube-URL.", "info");
    }
  };

  const extractVideoId = (url) => {
    try {
      const regExp =
        /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
      const match = url.match(regExp);
      return match && match[2].length === 11 ? match[2] : null;
    } catch (error) {
      console.error("Error extracting video ID:", error);
      return null;
    }
  };
  const fetchYoutubeTranscript = async (url, maxLength = 10000) => {
    try {
      setIsLoadingTranscript(true);
      setIsCancelled(false);

      // Use the same API_URL pattern you're already using
      const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";

      // Create new AbortController for this request
      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;

      // Make request to your backend API
      const response = await fetch(
        `${API_URL}/api/youtube/transcript?url=${encodeURIComponent(url)}&text=true`,
        { signal },
      );

      // Debug info
      console.log("Response status:", response.status);

      const data = await response.json();

      // Check for errors
      if (!response.ok || !data.content) {
        showSnackbar("Nåt gick fel. Försök hämta videon igen!", "error");
        return null;
      }

      let transcriptContent = data.content;

      // Truncate if needed
      if (transcriptContent.length > maxLength) {
        const lastPeriod = transcriptContent.lastIndexOf(".");
        transcriptContent =
          lastPeriod > maxLength * 0.8
            ? transcriptContent.substring(0, lastPeriod + 1)
            : transcriptContent.substring(0, maxLength);
      }
      await trackLesson(user.uid, "YouTubeSelector.js", null, null);

      return transcriptContent;
    } catch (error) {
      if (error.name === "AbortError") {
        return null;
      }
      console.error("Error fetching transcript:", error);
      showSnackbar(
        "Gick inte att hämta video från YouTube: " + error.message,
        "error",
      );
      return null;
    } finally {
      if (!isCancelled) {
        setIsLoadingTranscript(false);
      }
    }
  };

  const handleBackToInitialChoices = () => {
    setActiveStep(0);
    setSelectionMode(null);
  };

  const handleVideoSelect = async (video) => {
    setIsLoadingTranscript(true);
    const transcript = await fetchYoutubeTranscript(video.url);
    if (transcript && !isCancelled) {
      onTranscriptChange(
        `Gör ${
          forQuiz
            ? "ett quiz"
            : forFlashcards
              ? "flashcards"
              : forPowerPoint
                ? "en PowerPoint"
                : forPodcast
                  ? "en podcast"
                  : "en lektion"
        } baserat på innehållet i denna: ${video.title} Transkript: ${transcript}`,
      );

      // onTranscriptChange(
      //   `Gör en lektion baserat på innehållet i denna: ${video.title} Transkript: ${transcript} `
      // );
      setSelectedVideo(video);
      setTranscriptFetched(true);
      setActiveStep(2);
    }
    setIsLoadingTranscript(false);
  };

  const handleSubjectSelect = (subject) => {
    setSelectedSubject(subject);
    setActiveStep(1);
  };

  const handleStepClick = (step) => {
    if (step === 0) {
      handleReset();
    } else if (step === 1 && selectedSubject) {
      setTranscriptFetched(false);
      setSelectedVideo(null);
      setActiveStep(1);
    }
  };

  const handleReset = () => {
    setSelectedSubject(null);
    setTranscriptFetched(false);
    setSelectedVideo(null);
    setActiveStep(0);
  };

  return (
    <Container
      maxWidth="md"
      sx={{
        position: "relative",
      }}
    >
      {selectionMode === "curated" && !transcriptFetched && (
        <IconButton
          onClick={handleBackToInitialChoices}
          sx={{
            position: "absolute:",
            top: { xs: 0, md: -30 },
            left: { xs: -20, md: -47 },
          }}
        >
          <ArrowBackIcon />
        </IconButton>
      )}
      {selectionMode === null ? (
        // Initial choice view
        <Box sx={{ textAlign: "center" }}>
          <Typography
            variant="h5"
            sx={{
              mb: 1,
              fontSize: "1.5rem",
              fontWeight: "600",
              mt: { xs: 4, md: 3 },
            }}
          >
            Klistra in YouTube-länk
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{ mb: 3, color: "text.secondary" }}
          >
            T.ex. en föreläsning, intervju eller dokumentär
          </Typography>
          {/* New TextField directly in the UI */}
          <TextField
            autoFocus
            margin="dense"
            placeholder="https://www.youtube.com/watch?v=kBROYF3K4Zw"
            type="url"
            fullWidth
            variant="outlined"
            value={customUrl}
            onChange={(e) => setCustomUrl(e.target.value)}
            // Add this event handler for Enter key
            onKeyDown={(e) => {
              if (e.key === "Enter" && !customUrl) {
                showSnackbar("Klistra in en YouTube-länk i textfältet", "info");
                return;
              }

              if (e.key === "Enter" && customUrl) {
                handleCustomUrlSubmit();
              }
            }}
            sx={{
              mb: 3,
              maxWidth: { xs: "100%", md: "520px" },
              "& .MuiInputBase-root": { height: "60px" },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <YouTubeIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="Hämta video">
                    <div>
                      <IconButton
                        onClick={handleCustomUrlSubmit}
                        disabled={!customUrl}
                        sx={{
                          bgcolor: theme.palette.primary.main,
                          color: "white",
                          "&:hover": {
                            bgcolor: theme.palette.primary.main,
                          },
                          transition: "all 0.2s ease",
                        }}
                      >
                        <ArrowIcon fontSize="small" />
                      </IconButton>
                    </div>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
          <Grid container spacing={3} justifyContent="center">
            <Grid item xs={12} sm={12}>
              <Box
                sx={{
                  textAlign: "center",
                  mt: 8,
                  color: "text.secondary",
                  fontSize: "0.9rem",
                }}
              >
                Eller välj bland våra{" "}
                <Box
                  component="span"
                  sx={{
                    color: "primary.main",
                    cursor: "pointer",
                    fontWeight: 500,
                    textDecoration: "none",
                    display: "inline",
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  }}
                  onClick={() => setSelectionMode("curated")}
                >
                  utvalda videor
                </Box>
                .
              </Box>
            </Grid>
          </Grid>
        </Box>
      ) : (
        // Show the original content based on selection mode
        <>
          {selectionMode === "curated" && (
            <Box
              sx={{
                width: { xs: "100%", md: "100%" },
                margin: "0 auto",
                mb: 3,
              }}
            >
              <Stepper
                activeStep={activeStep}
                alternativeLabel
                sx={{
                  flex: 1,
                  "& .MuiStepIcon-root": {
                    color: "rgba(0, 0, 0, 0.38)",
                  },
                  "& .MuiStepIcon-root.Mui-active": {
                    color: "#7b68eebf",
                  },
                  "& .MuiStepIcon-root.Mui-completed": {
                    color: "#7b68ee",
                  },
                  "& .MuiStepLabel-label": {
                    fontSize: "0.95rem",
                  },
                }}
              >
                {steps.map((label, index) => (
                  <Step
                    key={label}
                    completed={index < activeStep}
                    sx={{
                      "& .MuiStepLabel-label:hover": {
                        textDecoration:
                          index < activeStep ? "underline" : "none",
                      },
                    }}
                  >
                    <StepLabel
                      onClick={() => handleStepClick(index)}
                      sx={{
                        cursor: index <= activeStep ? "pointer" : "default",
                        "&:hover":
                          index <= activeStep
                            ? { color: theme.palette.primary.main }
                            : {},
                      }}
                    >
                      {label}
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>
          )}

          {transcriptFetched ? (
            <Box
              sx={{
                display: "flex",
                gap: 3,
                alignItems: "center",
                p: 3,
                maxWidth: 900,
                mx: "auto",
              }}
            ></Box>
          ) : !selectedSubject ? (
            <>
              <Grid container spacing={2} sx={{ mt: 2 }}>
                {subjects.map((subject) => (
                  <Grid item xs={12} sm={6} md={4} key={subject.value}>
                    <ThemeFlowBox
                      icon={subject.Icon}
                      title={subject.label}
                      onClick={() => handleSubjectSelect(subject.value)}
                    />
                  </Grid>
                ))}
              </Grid>
            </>
          ) : (
            <Grid container spacing={3}>
              {(videos[selectedSubject] || []).map((video, index) => (
                <Grid item xs={12} sm={4} key={index}>
                  <VideoCard
                    video={video}
                    onSelect={() => handleVideoSelect(video)}
                    isDark={isDark}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </>
      )}
      {customVideo && (
        <Box sx={{ display: "none" }}>
          <VideoCard
            video={customVideo}
            onSelect={handleVideoSelect}
            isDark={isDark}
            showPreview={showCustomVideoPreview}
            setShowPreview={setShowCustomVideoPreview}
          />
        </Box>
      )}
      <LoadingDialog
        open={isLoadingTranscript}
        onClose={() => {}}
        progressLabel={"Hämtar YouTube-video..."}
        title="Hämtar innehållet..."
        snackbarMessage="Det är snart klart. Vänligen vänta."
        snackbarSeverity="info"
        progressBarHeight="15px"
        progressBarFontSize="0.9rem"
        hideProgressBar="true"
        user={user}
        coffee={false}
      />
    </Container>
  );
};

export default YouTubeSelector;
